import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LayoutDashboard, TrendingUp, Users, ShoppingCart, CreditCard, Euro, RefreshCw, ArrowLeft, Package, Building2, BarChart3, Filter, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  
  // Filtres
  const [period, setPeriod] = useState('30d');
  const [conciergeId, setConciergeId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [optionId, setOptionId] = useState('');
  const [moment, setMoment] = useState('');
  const [source, setSource] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Données pour les filtres
  const [concierges, setConcierges] = useState([]);
  const [properties, setProperties] = useState([]);
  const [options, setOptions] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (period !== 'custom') params.append('period', period);
      if (conciergeId) params.append('concierge_id', conciergeId);
      if (propertyId) params.append('property_id', propertyId);
      if (optionId) params.append('option_id', optionId);
      if (moment) params.append('moment', moment);
      if (source) params.append('source', source);
      if (period === 'custom' && startDate) params.append('start_date', startDate);
      if (period === 'custom' && endDate) params.append('end_date', endDate);
      
      const response = await api.get(`/admin/analytics?${params.toString()}`);
      setAnalytics(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accès réservé aux administrateurs');
        navigate('/dashboard');
      } else {
        toast.error('Erreur lors du chargement des analytics');
      }
    } finally {
      setLoading(false);
    }
  }, [period, conciergeId, propertyId, optionId, moment, source, startDate, endDate, navigate]);

  const fetchFilterData = async () => {
    try {
      const [conciRes, propRes, optRes] = await Promise.all([
        api.get('/admin/concierges'),
        api.get('/properties'),
        api.get('/admin/options')
      ]);
      setConcierges(conciRes.data || []);
      setProperties(propRes.data || []);
      setOptions(optRes.data || []);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  useEffect(() => {
    fetchFilterData();
    fetchAnalytics();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const resetFilters = () => {
    setPeriod('30d');
    setConciergeId('');
    setPropertyId('');
    setOptionId('');
    setMoment('');
    setSource('');
    setStartDate('');
    setEndDate('');
  };

  const momentLabels = {
    reservation: 'Réservation',
    pre_arrivee: 'Pré-arrivée',
    avant_depart: 'Avant départ',
    qr_code: 'QR Code'
  };

  const sourceLabels = {
    airbnb: 'Airbnb',
    booking: 'Booking',
    pms: 'PMS',
    email: 'Email',
    qr_code: 'QR Code',
    direct: 'Accès direct'
  };

  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-rose-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-4 sm:px-6 lg:px-8 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-6 h-6 text-rose-600" strokeWidth={1.5} />
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Analytics
              </span>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAnalytics}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filtres */}
        <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-slate-900 flex items-center">
              <Filter className="w-4 h-4 mr-2" />
              Filtres
            </h3>
            <Button variant="ghost" size="sm" onClick={resetFilters} className="text-slate-500 text-xs">
              Réinitialiser
            </Button>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* Période */}
            <div>
              <Label className="text-xs text-slate-500">Période</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="mt-1 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">7 derniers jours</SelectItem>
                  <SelectItem value="30d">30 derniers jours</SelectItem>
                  <SelectItem value="90d">90 derniers jours</SelectItem>
                  <SelectItem value="custom">Personnalisé</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dates personnalisées */}
            {period === 'custom' && (
              <>
                <div>
                  <Label className="text-xs text-slate-500">Du</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="mt-1 h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Au</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="mt-1 h-9"
                  />
                </div>
              </>
            )}

            {/* Conciergerie */}
            <div>
              <Label className="text-xs text-slate-500">Conciergerie</Label>
              <Select value={conciergeId || "all"} onValueChange={(v) => setConciergeId(v === "all" ? "" : v)}>
                <SelectTrigger className="mt-1 h-9">
                  <SelectValue placeholder="Toutes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  {concierges.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.entreprise || c.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Logement */}
            <div>
              <Label className="text-xs text-slate-500">Logement</Label>
              <Select value={propertyId || "all"} onValueChange={(v) => setPropertyId(v === "all" ? "" : v)}>
                <SelectTrigger className="mt-1 h-9">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Option */}
            <div>
              <Label className="text-xs text-slate-500">Option</Label>
              <Select value={optionId || "all"} onValueChange={(v) => setOptionId(v === "all" ? "" : v)}>
                <SelectTrigger className="mt-1 h-9">
                  <SelectValue placeholder="Toutes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  {options.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Moment */}
            <div>
              <Label className="text-xs text-slate-500">Moment</Label>
              <Select value={moment || "all"} onValueChange={(v) => setMoment(v === "all" ? "" : v)}>
                <SelectTrigger className="mt-1 h-9">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="reservation">Réservation</SelectItem>
                  <SelectItem value="pre_arrivee">Pré-arrivée</SelectItem>
                  <SelectItem value="avant_depart">Avant départ</SelectItem>
                  <SelectItem value="qr_code">QR Code</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Source */}
            <div>
              <Label className="text-xs text-slate-500">Source</Label>
              <Select value={source || "all"} onValueChange={(v) => setSource(v === "all" ? "" : v)}>
                <SelectTrigger className="mt-1 h-9">
                  <SelectValue placeholder="Toutes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="airbnb">Airbnb</SelectItem>
                  <SelectItem value="booking">Booking</SelectItem>
                  <SelectItem value="pms">PMS</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="qr_code">QR Code</SelectItem>
                  <SelectItem value="direct">Accès direct</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {analytics && (
          <>
            {/* Tunnel de Conversion */}
            <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-rose-600" />
                Tunnel de Conversion
              </h3>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Users className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                  <p className="text-2xl font-bold text-blue-700">{analytics.funnel.page_views}</p>
                  <p className="text-xs text-blue-600">Visites</p>
                </div>
                
                <div className="text-center p-4 bg-indigo-50 rounded-lg">
                  <Users className="w-6 h-6 mx-auto mb-2 text-indigo-600" />
                  <p className="text-2xl font-bold text-indigo-700">{analytics.funnel.unique_visitors}</p>
                  <p className="text-xs text-indigo-600">Visiteurs uniques</p>
                </div>
                
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <Package className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                  <p className="text-2xl font-bold text-purple-700">{analytics.funnel.option_selects}</p>
                  <p className="text-xs text-purple-600">Ajouts panier</p>
                </div>
                
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <ShoppingCart className="w-6 h-6 mx-auto mb-2 text-orange-600" />
                  <p className="text-2xl font-bold text-orange-700">{analytics.funnel.checkout_starts}</p>
                  <p className="text-xs text-orange-600">Checkouts</p>
                </div>
                
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <CreditCard className="w-6 h-6 mx-auto mb-2 text-green-600" />
                  <p className="text-2xl font-bold text-green-700">{analytics.funnel.payments}</p>
                  <p className="text-xs text-green-600">Paiements</p>
                </div>
                
                <div className="text-center p-4 bg-rose-50 rounded-lg">
                  <TrendingUp className="w-6 h-6 mx-auto mb-2 text-rose-600" />
                  <p className="text-2xl font-bold text-rose-700">{analytics.funnel.conversion_rate}%</p>
                  <p className="text-xs text-rose-600">Taux conversion</p>
                </div>
              </div>
            </Card>

            {/* Indicateurs Financiers */}
            <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
                <Euro className="w-5 h-5 mr-2 text-rose-600" />
                Indicateurs Financiers
              </h3>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="p-4 border border-slate-200 rounded-lg">
                  <p className="text-sm text-slate-500">Chiffre d'affaires</p>
                  <p className="text-2xl font-bold text-slate-900">{analytics.financial.total_revenue.toFixed(2)} €</p>
                </div>
                
                <div className="p-4 border border-slate-200 rounded-lg">
                  <p className="text-sm text-slate-500">Part Plus</p>
                  <p className="text-2xl font-bold text-rose-600">{analytics.financial.plus_share.toFixed(2)} €</p>
                </div>
                
                <div className="p-4 border border-slate-200 rounded-lg">
                  <p className="text-sm text-slate-500">Panier moyen</p>
                  <p className="text-2xl font-bold text-slate-900">{analytics.financial.avg_basket.toFixed(2)} €</p>
                </div>
                
                <div className="p-4 border border-slate-200 rounded-lg">
                  <p className="text-sm text-slate-500">Remboursements</p>
                  <p className="text-2xl font-bold text-orange-600">{analytics.financial.refunds_count}</p>
                </div>
                
                <div className="p-4 border border-slate-200 rounded-lg">
                  <p className="text-sm text-slate-500">Montant remboursé</p>
                  <p className="text-2xl font-bold text-orange-600">{analytics.financial.refunds_amount.toFixed(2)} €</p>
                </div>
              </div>
            </Card>

            {/* Stats par Moment et Source */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Par Moment */}
              <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-rose-600" />
                  Performance par Moment d'envoi
                </h3>
                {Object.keys(analytics.by_moment).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(analytics.by_moment)
                      .sort((a, b) => (b[1].revenue || 0) - (a[1].revenue || 0))
                      .map(([key, data]) => (
                      <div key={key} className="p-3 bg-slate-50 rounded-lg" data-testid={`moment-stats-${key}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-700">{momentLabels[key] || (key === 'direct' ? 'Accès direct' : key)}</span>
                          {data.conversion_rate > 0 && (
                            <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">
                              {data.conversion_rate}% conv.
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                          <div className="text-center p-2 bg-white rounded border border-slate-100">
                            <p className="text-blue-600 font-semibold">{data.visits || 0}</p>
                            <p className="text-slate-500">Visites</p>
                          </div>
                          <div className="text-center p-2 bg-white rounded border border-slate-100">
                            <p className="text-green-600 font-semibold">{data.payments || 0}</p>
                            <p className="text-slate-500">Paiements</p>
                          </div>
                          <div className="text-center p-2 bg-white rounded border border-slate-100">
                            <p className="text-slate-800 font-semibold">{(data.revenue || 0).toFixed(2)} €</p>
                            <p className="text-slate-500">CA</p>
                          </div>
                          <div className="text-center p-2 bg-white rounded border border-slate-100">
                            <p className="text-rose-600 font-semibold">{(data.plus_share || 0).toFixed(2)} €</p>
                            <p className="text-slate-500">Part Plus</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Aucune donnée avec paramètre "moment"</p>
                )}
              </Card>

              {/* Par Source */}
              <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
                  <LayoutDashboard className="w-5 h-5 mr-2 text-rose-600" />
                  Par Source
                </h3>
                {Object.keys(analytics.by_source).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(analytics.by_source).map(([key, data]) => (
                      <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="font-medium text-slate-700">{sourceLabels[key] || key}</span>
                        <div className="flex space-x-4 text-sm">
                          <span className="text-blue-600">{data.visits} visites</span>
                          <span className="text-green-600">{data.payments} paiements</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Aucune donnée avec paramètre "source"</p>
                )}
              </Card>
            </div>

            {/* Stats par Entité */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Par Conciergerie */}
              {analytics.by_concierge.length > 0 && (
                <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Top Conciergeries</h3>
                  <div className="space-y-2">
                    {analytics.by_concierge.slice(0, 5).map((c, idx) => (
                      <div key={c.id} className="flex items-center justify-between p-2 border-b border-slate-100 last:border-0">
                        <div className="flex items-center">
                          <span className="w-5 h-5 bg-rose-100 text-rose-600 rounded-full text-xs flex items-center justify-center mr-2">{idx + 1}</span>
                          <span className="text-sm text-slate-700 truncate max-w-[120px]">{c.entreprise || c.nom}</span>
                        </div>
                        <span className="font-semibold text-slate-900">{c.revenue.toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Par Logement */}
              {analytics.by_property.length > 0 && (
                <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Top Logements</h3>
                  <div className="space-y-2">
                    {analytics.by_property.slice(0, 5).map((p, idx) => (
                      <div key={p.id} className="flex items-center justify-between p-2 border-b border-slate-100 last:border-0">
                        <div className="flex items-center">
                          <span className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full text-xs flex items-center justify-center mr-2">{idx + 1}</span>
                          <span className="text-sm text-slate-700 truncate max-w-[120px]">{p.nom}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-slate-900">{p.revenue.toFixed(2)} €</span>
                          <span className="text-xs text-slate-500 block">{p.visits} visites</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Par Option */}
              {analytics.by_option.length > 0 && (
                <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Top Options</h3>
                  <div className="space-y-2">
                    {analytics.by_option.slice(0, 5).map((o, idx) => (
                      <div key={o.id} className="flex items-center justify-between p-2 border-b border-slate-100 last:border-0">
                        <div className="flex items-center">
                          <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full text-xs flex items-center justify-center mr-2">{idx + 1}</span>
                          <span className="text-sm text-slate-700 truncate max-w-[120px]">{o.nom}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-slate-900">{o.sales_count} ventes</span>
                          <span className="text-xs text-slate-500 block">{o.revenue.toFixed(2)} €</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>

            {/* Évolution par jour */}
            {Object.keys(analytics.by_day).length > 0 && (
              <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 mt-6">
                <h3 className="font-semibold text-slate-900 mb-4">Évolution journalière</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-slate-500 font-medium">Date</th>
                        <th className="text-right py-2 px-3 text-slate-500 font-medium">Visites</th>
                        <th className="text-right py-2 px-3 text-slate-500 font-medium">Checkouts</th>
                        <th className="text-right py-2 px-3 text-slate-500 font-medium">Paiements</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(analytics.by_day).slice(-14).map(([date, data]) => (
                        <tr key={date} className="border-b border-slate-100 last:border-0">
                          <td className="py-2 px-3 text-slate-700">{new Date(date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                          <td className="py-2 px-3 text-right text-blue-600">{data.page_views}</td>
                          <td className="py-2 px-3 text-right text-orange-600">{data.checkouts}</td>
                          <td className="py-2 px-3 text-right text-green-600">{data.payments}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminAnalytics;
